// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName, type MedplumClient } from '@medplum/core';
import type { HealthcareService, Practitioner } from '@medplum/fhirtypes';
import type { AppointmentTypeDefinition } from '@polarissleep/scheduling';
import {
  type AvailabilitySearchRepository,
  getTimezoneExtensionValue,
  type SchedulingScope,
  searchAvailability,
} from '@polarissleep/scheduling-next';
import { getProviderAppEnvConfig } from '../config/env';
import { createProviderSchedulingClient } from './client';
import type { ProviderAppointmentCandidate } from './types';

export async function findProviderAppointments(
  medplum: MedplumClient,
  params: {
    appointmentType: AppointmentTypeDefinition;
    startDate: string;
    endDate: string;
    practitionerId?: string;
    signal?: AbortSignal;
  }
): Promise<ProviderAppointmentCandidate[]> {
  const config = getProviderAppEnvConfig();
  if (!config.medplumOrganizationId || !config.polarisEnv) {
    throw new Error('Provider v2 scheduling requires a tenant organization and Polaris environment.');
  }
  const scope: SchedulingScope = { organizationId: config.medplumOrganizationId, environment: config.polarisEnv };
  const result = await searchAvailability(
    createProviderAvailabilityRepository(medplum),
    createProviderSchedulingClient(medplum),
    { ...params, appointmentType: params.appointmentType, start: params.startDate, end: params.endDate, scope },
    { now: () => new Date() }
  );
  const providersById = new Map(
    result.providers.map((provider) => [
      provider.practitioner.id ?? '',
      {
        name: provider.practitioner.name?.[0] ? formatHumanName(provider.practitioner.name[0]) : 'Provider',
        timezone: requireProviderTimezone(provider.practitioner.id, provider.practitioner.extension),
        service: provider.healthcareService,
      },
    ])
  );
  return result.appointments.map((appointment) => {
    const providerReference = appointment.participant?.find((participant) =>
      participant.actor?.reference?.startsWith('Practitioner/')
    )?.actor?.reference;
    const providerId = providerReference?.replace('Practitioner/', '') ?? '';
    const provider = providersById.get(providerId);
    if (!provider) {
      throw new Error('Proposed Appointment does not identify an eligible provider with a scheduling timezone.');
    }
    const serviceReference = appointment.serviceType?.flatMap((serviceType) =>
      (serviceType.extension ?? [])
        .filter((extension) => extension.url === 'https://medplum.com/fhir/service-type-reference')
        .map((extension) => extension.valueReference?.reference)
    )[0];
    const serviceId = serviceReference?.replace('HealthcareService/', '') ?? provider?.service.id ?? '';
    const service = result.providers.find(
      (candidate) => candidate.healthcareService.id === serviceId
    )?.healthcareService;
    return {
      appointment,
      key: `${providerId}|${serviceId}|${appointment.start ?? ''}|${appointment.end ?? ''}`,
      providerId,
      providerName: provider.name,
      providerTimezone: provider.timezone,
      serviceId,
      serviceName: service?.name ?? appointment.serviceType?.[0]?.coding?.[0]?.display ?? 'Appointment',
      serviceCode: appointment.serviceType?.[0]?.coding?.[0]?.code ?? '',
    };
  });
}

function requireProviderTimezone(providerId: string | undefined, extensions: Practitioner['extension']): string {
  const timezone = getTimezoneExtensionValue(extensions);
  if (!timezone) {
    throw new Error(`Practitioner/${providerId ?? 'unknown'} has no valid scheduling timezone.`);
  }
  return timezone;
}

function createProviderAvailabilityRepository(medplum: MedplumClient): AvailabilitySearchRepository {
  return {
    async *searchResourcePages(resourceType, search) {
      let resources = (await medplum.searchResources(resourceType, search, { cache: 'no-cache' })) as Array<
        HealthcareService | Practitioner
      >;

      if (resourceType === 'HealthcareService' && resources.length === 0 && search.identifier) {
        const { identifier, ...scopedSearch } = search;
        const scopedResources = (await medplum.searchResources(resourceType, scopedSearch, {
          cache: 'no-cache',
        })) as HealthcareService[];
        const [system, value] = identifier.split('|');
        resources = scopedResources.filter((service) =>
          service.identifier?.some((item) => item.system === system && item.value === value)
        );
      }
      yield resources as never;
    },
  };
}
